/**
 * Active-event session context (FR-EVENTS-08).
 *
 * Every event-scoped screen — Play Entry, Coaches Highlight, Game Summary, Play
 * Archive, Stat Tracker — reads "which event am I looking at?" from here rather
 * than from its own URL, so the header switcher changes all of them at once.
 *
 * Three guarantees:
 *   1. **Persistence.** The choice is written to `localStorage` and restored on
 *      the next paint, so a sideline tablet that reloads mid-game comes back to
 *      the same event without a round trip. Signing out clears it.
 *   2. **Revalidation.** The cached row is re-read from `GET /events/v1/{id}`
 *      once per mount, so a status flip (someone closed the game, or the hourly
 *      auto-close job did) shows up without a manual refresh. A 403/404 clears
 *      the stale selection instead of pinning the UI to an event that is gone.
 *   3. **A decoupling seam.** Each change dispatches
 *      `APP_EVENTS.activeEventChanged` on `window`, which the live-update client
 *      subscribes to in order to re-route its WebSocket channel — no React
 *      dependency between the switcher and the transport.
 *   4. **Tenant scope.** The selection is pinned to the tenant it was made
 *      under (the session's `tid`, or a Fanatic's selected grant). A persisted
 *      event from another scope is discarded on load, and the selection is
 *      cleared the moment the scope changes (a Fanatic picking a different
 *      team), so the header never names an event of a team the current
 *      session cannot open (docs/TENANT_ISOLATION.md, "URL slug validation").
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { ApiClient } from '../lib/api-client';
import { ApiError } from '../lib/api-error';
import type { TeamEvent } from '../lib/api-types';
import { APP_EVENTS, STORAGE_KEYS } from '../lib/constants';
import { readPreference, writePreference } from '../lib/session-storage';

import { useSession } from './session-context';

export interface ActiveEventContextValue {
  /** `null` when nothing is selected yet — screens must handle that state. */
  activeEvent: TeamEvent | null;
  activeEventId: string | null;
  /** True while the persisted selection is being revalidated. */
  isRevalidating: boolean;
  /** Human-readable failure from the last revalidation, if any. */
  error: string | null;
  /** Select (or clear) the session's event. Persists + broadcasts. */
  setActiveEvent: (event: TeamEvent | null) => void;
  /** Replace the cached row in place, e.g. right after a close or reopen. */
  refreshActiveEvent: (event: TeamEvent) => void;
}

const ActiveEventContext = createContext<ActiveEventContextValue | null>(null);

/** Defensive: a 204 or a proxy hiccup must not be cached as an "event". */
function isTeamEvent(value: unknown): value is TeamEvent {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<TeamEvent>;
  return typeof record.event_id === 'string' && typeof record.name === 'string';
}

/**
 * Restore the persisted selection, but only if it was made under `scope`.
 * A selection with no recorded scope (written before scoping existed) is
 * accepted once and stamped on the next write. While the scope itself is
 * still unresolved (`null` — a Fanatic whose team is being looked up from
 * their grants) the decision is deferred to the scope-change effect.
 */
function loadStoredEvent(scope: string | null): TeamEvent | null {
  const raw = readPreference(STORAGE_KEYS.activeEvent);
  if (raw === null) return null;
  const storedScope = readPreference(STORAGE_KEYS.activeEventScope);
  if (scope !== null && storedScope !== null && storedScope !== scope) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isTeamEvent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function storeEvent(event: TeamEvent | null, scope: string | null): void {
  writePreference(STORAGE_KEYS.activeEvent, event === null ? null : JSON.stringify(event));
  writePreference(STORAGE_KEYS.activeEventScope, event === null ? null : scope);
}

function broadcast(eventId: string | null): void {
  globalThis.dispatchEvent?.(
    new CustomEvent(APP_EVENTS.activeEventChanged, { detail: { eventId } })
  );
}

export function ActiveEventProvider({
  apiClient,
  children,
}: {
  /** Injected rather than pulled from `useApiClient()`, which would cycle. */
  apiClient: ApiClient;
  children: ReactNode;
}): JSX.Element {
  const { isAuthenticated, session, persona, activeTenant } = useSession();
  // The tenant every API call from this session is scoped to: the token's
  // `tid` for tenant personas, the selected grant for a Fanatic.
  const scope =
    persona === 'fanatic' ? (activeTenant?.tenantId ?? null) : (session?.tenantId ?? null);
  const [activeEvent, setActiveEventState] = useState<TeamEvent | null>(() =>
    loadStoredEvent(scope)
  );
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeEventId = activeEvent?.event_id ?? null;
  const activeIdRef = useRef<string | null>(activeEventId);
  activeIdRef.current = activeEventId;
  const scopeRef = useRef<string | null>(scope);

  const setActiveEvent = useCallback(
    (event: TeamEvent | null) => {
      // Updated eagerly (not just on render) so an in-flight revalidation that
      // resolves before React re-renders cannot resurrect a cleared selection.
      activeIdRef.current = event?.event_id ?? null;
      setActiveEventState(event);
      setError(null);
      storeEvent(event, scope);
      broadcast(event?.event_id ?? null);
    },
    [scope]
  );

  const refreshActiveEvent = useCallback(
    (event: TeamEvent) => {
      if (activeIdRef.current !== event.event_id) return;
      setActiveEventState(event);
      storeEvent(event, scope);
    },
    [scope]
  );

  // Signing out must not leave the next user looking at the previous team's game.
  useEffect(() => {
    if (isAuthenticated || activeIdRef.current === null) return;
    activeIdRef.current = null;
    setActiveEventState(null);
    storeEvent(null, null);
    broadcast(null);
  }, [isAuthenticated]);

  // Changing tenant scope (a Fanatic selecting another granted team) drops the
  // selection at once — the header must never carry one team's event into
  // another team's chrome, not even until revalidation answers 403. A scope
  // RESOLVING for the first time (null → team) is not a switch: the restored
  // selection is kept when it was recorded under that team (or before scoping
  // existed) and dropped when it was recorded under another.
  useEffect(() => {
    const previous = scopeRef.current;
    if (previous === scope) return;
    scopeRef.current = scope;
    if (activeIdRef.current === null) return;
    if (previous === null && scope !== null) {
      const storedScope = readPreference(STORAGE_KEYS.activeEventScope);
      if (storedScope === null || storedScope === scope) {
        writePreference(STORAGE_KEYS.activeEventScope, scope);
        return;
      }
    }
    activeIdRef.current = null;
    setActiveEventState(null);
    setError(null);
    storeEvent(null, null);
    broadcast(null);
  }, [scope]);

  // Revalidate the restored selection exactly once per id, not on every render.
  useEffect(() => {
    if (!isAuthenticated || activeEventId === null) return undefined;
    const controller = new AbortController();
    setIsRevalidating(true);

    apiClient
      .getEvent(activeEventId, controller.signal)
      .then((fresh) => {
        if (controller.signal.aborted || !isTeamEvent(fresh)) return;
        // The selection may have been cleared (sign-out, scope change) while
        // the request was in flight — never resurrect it from the response.
        if (activeIdRef.current !== fresh.event_id) return;
        setActiveEventState((current) =>
          current !== null && current.event_id === fresh.event_id ? fresh : current
        );
        storeEvent(fresh, scopeRef.current);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        const isGone =
          ApiError.isApiError(caught) && (caught.status === 404 || caught.status === 403);
        if (isGone) {
          activeIdRef.current = null;
          storeEvent(null, null);
          setActiveEventState(null);
          broadcast(null);
          setError('The event you were working on is no longer available. Pick another one.');
          return;
        }
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'The active event could not be refreshed. Showing the last known details.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsRevalidating(false);
      });

    return () => controller.abort();
  }, [apiClient, isAuthenticated, activeEventId]);

  const value = useMemo<ActiveEventContextValue>(
    () => ({
      activeEvent,
      activeEventId,
      isRevalidating,
      error,
      setActiveEvent,
      refreshActiveEvent,
    }),
    [activeEvent, activeEventId, error, isRevalidating, refreshActiveEvent, setActiveEvent]
  );

  return <ActiveEventContext.Provider value={value}>{children}</ActiveEventContext.Provider>;
}

export function useActiveEvent(): ActiveEventContextValue {
  const value = useContext(ActiveEventContext);
  if (value === null) throw new ActiveEventContextError();
  return value;
}

class ActiveEventContextError extends Error {
  constructor() {
    super('useActiveEvent() must be called inside <ActiveEventProvider>.');
    this.name = 'ActiveEventContextError';
  }
}
